import { ACOUSTIC_DEFAULTS, WINDOW_SECONDS } from './config';
import { getAcousticConfig } from './acousticRuntimeConfig';
import type { AcousticRoomSignal } from '../liveLessonTypes';
import { LowLevelFeatureExtractor } from './lowLevelFeatures';
import { buildBandFrameFeatures } from './frequencyBands';
import { deriveSocialAcousticFeatures, mapDimensionsToGlobalFeatures } from './socialAcousticFeatures';
import { TemporalAggregator } from './temporalAggregation';
import { computeSpeechModeScores, detectClassroomAcousticModes } from './speechModeFusion';
import { SituationStateMachine } from './stateMachine';
import { computeConfidence } from './confidenceModel';
import { YamnetClassifier, type GroupedYamnetScores } from './ml/yamnetClassifier';
import { clamp01, getTrend, pushLimited, smoothLevelDbEma, standardDeviation, mean } from './utils';
import type { ScoredSituation, SituationKey } from './types';

const SPEECH_MODE_KEYS: SituationKey[] = ['single_speaker', 'conversation', 'babble', 'shouting'];

const EMPTY_YAMNET: GroupedYamnetScores = {
  speech: 0,
  conversation: 0,
  babble: 0,
  shouting: 0
};

type AcousticEngineOptions = {
  sampleRate?: number;
};

function calculatePausePulseScore(series: number[]): number {
  if (series.length < 8) return 0;
  const avg = mean(series);
  const std = standardDeviation(series);
  const highThreshold = avg + std * 1.15;
  const lowThreshold = Math.max(0.01, avg - std * 0.55);
  const minGapFrames = 2;

  let peakCount = 0;
  let lastPeakIndex = -100;
  for (let i = 1; i < series.length - 1; i += 1) {
    const value = series[i];
    const isPeak = value > highThreshold && value >= series[i - 1] && value >= series[i + 1];
    if (isPeak && i - lastPeakIndex >= minGapFrames) {
      peakCount += 1;
      lastPeakIndex = i;
    }
  }

  let quietFrames = 0;
  for (let i = 0; i < series.length; i += 1) {
    if (series[i] <= lowThreshold) quietFrames += 1;
  }
  const quietRatio = quietFrames / series.length;
  const normalizedPeakRate = clamp01(peakCount / (series.length / 3));

  return clamp01(normalizedPeakRate * 0.55 + quietRatio * 0.45);
}

function emptyFrequencyDb(binCount: number): Float32Array {
  return new Float32Array(binCount);
}

export class SpeechModeAcousticEngine {
  private readonly sampleRate: number;

  private readonly lowLevelExtractor = new LowLevelFeatureExtractor();

  private readonly temporal = new TemporalAggregator();

  private readonly stateMachine: SituationStateMachine;

  private readonly yamnet = new YamnetClassifier();

  private loudnessHistory: number[] = [];

  private displayLevelDbEma: number | null = null;

  private previousLoudness = 0;

  private lastYamnetScores: GroupedYamnetScores = { ...EMPTY_YAMNET };

  private speechBuffer: number[] = [];

  private speechActive = false;

  private nonSpeechFrameCount = 0;

  private lastYamnetInferenceAt = 0;

  private yamnetInferenceInFlight = false;

  private lastDimensions: ReturnType<typeof deriveSocialAcousticFeatures> | null = null;

  private lastPausePulseScore = 0;

  private speechContextUntil = 0;

  private lastStablePhaseFit: AcousticRoomSignal['phaseFit'] = 'low';

  private onSignal: ((signal: AcousticRoomSignal) => void) | null = null;

  private extendSpeechContext(now = Date.now()): void {
    this.speechContextUntil = now + ACOUSTIC_DEFAULTS.speechContextHoldMs;
  }

  private isInSpeechContext(now = Date.now()): boolean {
    return now < this.speechContextUntil;
  }

  constructor(options: AcousticEngineOptions = {}) {
    this.sampleRate = options.sampleRate ?? ACOUSTIC_DEFAULTS.targetSampleRate;
    const frameDurationSec = ACOUSTIC_DEFAULTS.vadFrameSamples / this.sampleRate;
    this.temporal.configure(frameDurationSec);
    this.stateMachine = new SituationStateMachine({ frameDurationSec });
  }

  setSignalListener(listener: ((signal: AcousticRoomSignal) => void) | null): void {
    this.onSignal = listener;
  }

  get frameIntervalMs(): number {
    return Math.round((ACOUSTIC_DEFAULTS.vadFrameSamples / this.sampleRate) * 1000);
  }

  async initialize(): Promise<void> {
    try {
      await this.yamnet.ensureLoaded();
    } catch {
      // YAMNet optional: Meyda + VAD fusion still works without the model file.
    }
  }

  isYamnetLoaded(): boolean {
    return this.yamnet.isLoaded();
  }

  reset(): void {
    this.lowLevelExtractor.reset();
    this.temporal.reset();
    this.stateMachine.reset();
    this.loudnessHistory = [];
    this.displayLevelDbEma = null;
    this.previousLoudness = 0;
    this.lastYamnetScores = { ...EMPTY_YAMNET };
    this.speechBuffer = [];
    this.speechActive = false;
    this.nonSpeechFrameCount = 0;
    this.lastYamnetInferenceAt = 0;
    this.yamnetInferenceInFlight = false;
    this.lastDimensions = null;
    this.lastPausePulseScore = 0;
    this.speechContextUntil = 0;
    this.lastStablePhaseFit = 'low';
  }

  processVadFrame(isSpeech: boolean, frame: Float32Array): AcousticRoomSignal {
    if (isSpeech) {
      this.extendSpeechContext();
      this.speechActive = true;
      this.nonSpeechFrameCount = 0;
      this.speechBuffer.push(...frame);
      const maxBuffer = ACOUSTIC_DEFAULTS.yamnetWindowSamples * 3;
      if (this.speechBuffer.length > maxBuffer) {
        this.speechBuffer = this.speechBuffer.slice(-maxBuffer);
      }
      void this.maybeRunYamnetInference();
    } else {
      this.nonSpeechFrameCount += 1;
      if (this.nonSpeechFrameCount >= 8 && !this.isInSpeechContext()) {
        this.speechActive = false;
        this.speechBuffer = [];
      }
    }

    return this.buildSignal(isSpeech, frame);
  }

  async processSpeechSegment(audio: Float32Array): Promise<AcousticRoomSignal> {
    this.extendSpeechContext();
    if (audio.length) {
      try {
        this.lastYamnetScores = await this.yamnet.classifyAudio(audio);
        this.lastYamnetInferenceAt = Date.now();
      } catch {
        // keep previous scores
      }
    }
    const signal = this.buildSignal(true);
    this.onSignal?.(signal);
    return signal;
  }

  private async maybeRunYamnetInference(): Promise<void> {
    if (this.yamnetInferenceInFlight || !this.yamnet.isLoaded()) return;
    const now = Date.now();
    if (now - this.lastYamnetInferenceAt < ACOUSTIC_DEFAULTS.yamnetInferenceThrottleMs) return;
    if (this.speechBuffer.length < Math.floor(ACOUSTIC_DEFAULTS.yamnetWindowSamples * 0.5)) return;

    this.yamnetInferenceInFlight = true;
    const samples = Float32Array.from(
      this.speechBuffer.slice(-ACOUSTIC_DEFAULTS.yamnetWindowSamples)
    );
    try {
      this.lastYamnetScores = await this.yamnet.classifyAudio(samples);
      this.lastYamnetInferenceAt = now;
      const signal = this.buildSignal(true);
      this.onSignal?.(signal);
    } catch {
      // ignore inference errors
    } finally {
      this.yamnetInferenceInFlight = false;
    }
  }

  private buildSignal(hasSpeechFrame: boolean, vadFrame?: Float32Array): AcousticRoomSignal {
    const frame =
      vadFrame?.length === ACOUSTIC_DEFAULTS.vadFrameSamples
        ? vadFrame
        : this.speechBuffer.length
        ? Float32Array.from(this.speechBuffer.slice(-ACOUSTIC_DEFAULTS.fftSize))
        : new Float32Array(ACOUSTIC_DEFAULTS.fftSize);
    const frequencyDb = emptyFrequencyDb(Math.max(1, Math.floor(frame.length / 2)));
    const lowLevel = this.lowLevelExtractor.extract(frame, frequencyDb, this.sampleRate);
    const frameDurationSec = ACOUSTIC_DEFAULTS.vadFrameSamples / this.sampleRate;
    this.temporal.configure(frameDurationSec);
    this.stateMachine.configureFrameDuration(frameDurationSec);

    const maxLoudnessHistory = Math.ceil((WINDOW_SECONDS.long + 5) / frameDurationSec);
    this.loudnessHistory = pushLimited(this.loudnessHistory, lowLevel.loudnessNormalized, maxLoudnessHistory);
    const displayLevelDb =
      this.displayLevelDbEma === null
        ? lowLevel.levelDb
        : smoothLevelDbEma(
            this.displayLevelDbEma,
            lowLevel.levelDb,
            frameDurationSec,
            ACOUSTIC_DEFAULTS.displayLevelDbWindowSeconds
          );
    this.displayLevelDbEma = displayLevelDb;
    const shortLoudnessHistory = this.loudnessHistory.slice(-Math.max(6, Math.ceil(WINDOW_SECONDS.short / frameDurationSec)));
    const pausePulseScore = calculatePausePulseScore(shortLoudnessHistory);
    this.lastPausePulseScore = pausePulseScore;

    const bandFrame = buildBandFrameFeatures(lowLevel.powerSpectrum, this.sampleRate);
    const interimBandAggregation = bandFrame.map((band) => ({
      ...band,
      meanEnergy: band.energy,
      variance: 0,
      volatility: 0,
      trend: 'stable' as const,
      peakRate: 0,
      persistence: 0
    }));

    const dimensions = deriveSocialAcousticFeatures({
      lowLevel,
      bands: interimBandAggregation,
      loudnessHistoryShort: shortLoudnessHistory
    });
    this.lastDimensions = dimensions;

    const volatilityInput = clamp01(standardDeviation(shortLoudnessHistory) * 3.2);
    const inSpeechContext = this.isInSpeechContext();
    const acousticCfg = getAcousticConfig();
    const roomActivity =
      lowLevel.loudnessNormalized >= acousticCfg.roomActivityLoudnessMin &&
      (dimensions.noiseIntensity >= acousticCfg.roomActivityNoiseMin ||
        dimensions.diffuseness >= acousticCfg.babbleDiffusenessMin ||
        dimensions.turnTaking >= acousticCfg.babbleTurnTakingMin);
    const effectiveHasSpeech = hasSpeechFrame || this.speechActive || inSpeechContext || roomActivity;
    const fusionInput = {
      yamnet: this.lastYamnetScores,
      loudness: lowLevel.loudnessNormalized,
      noiseIntensity: dimensions.noiseIntensity,
      turnTaking: dimensions.turnTaking,
      diffuseness: dimensions.diffuseness,
      speechCoherence: dimensions.speechCoherence,
      pausePulseScore,
      crestFactor: lowLevel.crestFactor,
      peakDensity: lowLevel.peakDensity,
      hasSpeech: effectiveHasSpeech
    };
    const speechMode = computeSpeechModeScores(fusionInput, acousticCfg);
    const classroomModes = detectClassroomAcousticModes(fusionInput, acousticCfg);
    const globalFeatures = mapDimensionsToGlobalFeatures(dimensions, lowLevel);

    this.temporal.push(
      {
        ...globalFeatures,
        speechCoherence: dimensions.speechCoherence,
        noiseIntensity: dimensions.noiseIntensity
      },
      bandFrame,
      speechMode.sortedScores
    );

    const shortLoudnessStats = this.temporal.getFeatureStats('loudness', 'short');
    const longLoudnessStats = this.temporal.getFeatureStats('loudness', 'long');
    const shortBands = this.temporal.getBandAggregation('short');
    const situationBands = this.temporal.getBandAggregation('situation');

    const integratedSituationScores: ScoredSituation[] = SPEECH_MODE_KEYS.map((key) => {
      const meanScore = this.temporal.getSituationStatsBySeconds(key, WINDOW_SECONDS.integration).mean;
      const label = speechMode.sortedScores.find((entry) => entry.key === key)?.label ?? key;
      return {
        key,
        label,
        score: Number(meanScore.toFixed(3))
      };
    }).sort((a, b) => b.score - a.score);

    const state = effectiveHasSpeech
      ? this.stateMachine.update(integratedSituationScores)
      : { state: 'silence' as const, persistence: 0, transitionLikelihood: 0.1 };

    const topSituation = integratedSituationScores[0];
    const temporalStability = topSituation ? this.temporal.getSituationStats(topSituation.key, 'situation').stability : 0;
    const confidence = computeConfidence({
      sortedScores: integratedSituationScores,
      dimensions,
      temporalStability,
      persistence: state.persistence,
      transitionLikelihood: state.transitionLikelihood
    });

    const volatilityShort = clamp01(Math.sqrt(shortLoudnessStats.variance) * 3.2);
    const volatilityLong = clamp01(Math.sqrt(longLoudnessStats.variance) * 3.2);
    const changeScore = clamp01(
      Math.abs(lowLevel.loudnessNormalized - this.previousLoudness) * 0.45 +
        volatilityShort * 0.35 +
        Math.min(1, Math.abs(shortLoudnessStats.slope) * 30) * 0.2
    );
    this.previousLoudness = lowLevel.loudnessNormalized;

    const spectralStability = clamp01(
      1 -
        (shortBands.reduce((acc, band) => acc + Math.sqrt(band.variance), 0) / Math.max(1, shortBands.length)) * 2.5
    );

    let detectedSituation: AcousticRoomSignal['detectedSituation'] = state.state;
    if (!effectiveHasSpeech) {
      detectedSituation = 'silence';
    } else if (detectedSituation === 'unclear' && topSituation && topSituation.score >= 0.33) {
      detectedSituation = topSituation.key;
    }

    const loudnessForPhase =
      inSpeechContext && !hasSpeechFrame && shortLoudnessHistory.length
        ? mean(shortLoudnessHistory)
        : lowLevel.loudnessNormalized;

    let phaseFit: AcousticRoomSignal['phaseFit'] =
      loudnessForPhase < ACOUSTIC_DEFAULTS.phaseLowThreshold
        ? 'low'
        : loudnessForPhase > ACOUSTIC_DEFAULTS.phaseHighThreshold
        ? 'high'
        : 'ok';

    if (hasSpeechFrame) {
      this.lastStablePhaseFit = phaseFit;
    } else if (inSpeechContext) {
      phaseFit = this.lastStablePhaseFit;
    } else {
      this.lastStablePhaseFit = phaseFit;
    }

    const signal: AcousticRoomSignal = {
      capturedAt: new Date().toISOString(),
      levelDb: Number(displayLevelDb.toFixed(2)),
      loudnessNormalized: Number(lowLevel.loudnessNormalized.toFixed(3)),
      peakDensity: Number(lowLevel.peakDensity.toFixed(4)),
      volatility: Number(volatilityShort.toFixed(3)),
      volatilityShort: Number(volatilityShort.toFixed(3)),
      volatilityLong: Number(volatilityLong.toFixed(3)),
      trend: getTrend(shortLoudnessStats.slope, 0.008),
      trendShort: getTrend(shortLoudnessStats.slope, 0.008),
      trendLong: getTrend(longLoudnessStats.slope, 0.004),
      changeScore: Number(changeScore.toFixed(3)),
      rhythmRegularity: Number(this.temporal.getFeatureStats('rhythmicity', 'situation').autocorrelation.toFixed(3)),
      spectralStability: Number(spectralStability.toFixed(3)),
      bandMetrics: shortBands.map((band) => ({
        key: band.key,
        label: band.label,
        meaning: band.meaning,
        hzMin: band.hzMin,
        hzMax: band.hzMax,
        loudness: Number(band.meanEnergy.toFixed(3)),
        volatility: Number(band.volatility.toFixed(3)),
        trend: band.trend
      })),
      detectedSituation,
      situationConfidence: confidence.confidence,
      situationScores: integratedSituationScores.map((entry) => ({
        key: entry.key,
        label: entry.label,
        score: Number(entry.score.toFixed(3))
      })),
      phaseFit,
      transitionLikelihood: confidence.transitionLikelihood,
      competingSituations: confidence.competingSituations,
      globalFeatures: {
        loudness: Number(globalFeatures.loudness.toFixed(3)),
        volatility: Number(globalFeatures.volatility.toFixed(3)),
        entropy: Number(lowLevel.spectralEntropy.toFixed(3)),
        rhythmicity: Number(dimensions.rhythmicity.toFixed(3)),
        dominance: Number(dimensions.dominance.toFixed(3)),
        diffuseness: Number(dimensions.diffuseness.toFixed(3)),
        pausePulse: Number(pausePulseScore.toFixed(3))
      },
      situationScoreMap: {
        single_speaker: Number((integratedSituationScores.find((entry) => entry.key === 'single_speaker')?.score ?? 0).toFixed(3)),
        conversation: Number((integratedSituationScores.find((entry) => entry.key === 'conversation')?.score ?? 0).toFixed(3)),
        babble: Number((integratedSituationScores.find((entry) => entry.key === 'babble')?.score ?? 0).toFixed(3)),
        shouting: Number((integratedSituationScores.find((entry) => entry.key === 'shouting')?.score ?? 0).toFixed(3))
      },
      bandActivity: situationBands.map((band) => ({
        band: band.label,
        energy: Number(band.meanEnergy.toFixed(3)),
        volatility: Number(band.volatility.toFixed(3)),
        trend: band.trend,
        persistence: Number(band.persistence.toFixed(3))
      })),
      decisionProfile: {
        yamnet: {
          speech: Number(this.lastYamnetScores.speech.toFixed(3)),
          conversation: Number(this.lastYamnetScores.conversation.toFixed(3)),
          babble: Number(this.lastYamnetScores.babble.toFixed(3)),
          shouting: Number(this.lastYamnetScores.shouting.toFixed(3))
        },
        fusion: {
          loudness: Number(lowLevel.loudnessNormalized.toFixed(3)),
          noiseIntensity: Number(dimensions.noiseIntensity.toFixed(3)),
          turnTaking: Number(dimensions.turnTaking.toFixed(3)),
          diffuseness: Number(dimensions.diffuseness.toFixed(3)),
          speechCoherence: Number(dimensions.speechCoherence.toFixed(3)),
          pausePulse: Number(pausePulseScore.toFixed(3)),
          crestFactor: Number(lowLevel.crestFactor.toFixed(3)),
          peakDensity: Number(lowLevel.peakDensity.toFixed(4)),
          effectiveHasSpeech
        },
        frameFusionScores: {
          single_speaker: Number((speechMode.scoreMap.single_speaker ?? 0).toFixed(3)),
          conversation: Number((speechMode.scoreMap.conversation ?? 0).toFixed(3)),
          babble: Number((speechMode.scoreMap.babble ?? 0).toFixed(3)),
          shouting: Number((speechMode.scoreMap.shouting ?? 0).toFixed(3))
        },
        integratedScores: {
          single_speaker: Number(
            (integratedSituationScores.find((entry) => entry.key === 'single_speaker')?.score ?? 0).toFixed(3)
          ),
          conversation: Number(
            (integratedSituationScores.find((entry) => entry.key === 'conversation')?.score ?? 0).toFixed(3)
          ),
          babble: Number((integratedSituationScores.find((entry) => entry.key === 'babble')?.score ?? 0).toFixed(3)),
          shouting: Number(
            (integratedSituationScores.find((entry) => entry.key === 'shouting')?.score ?? 0).toFixed(3)
          )
        },
        classroomModes: { ...classroomModes }
      }
    };

    this.onSignal?.(signal);
    return signal;
  }
}

/** @deprecated Use SpeechModeAcousticEngine */
export const SocialAcousticStateEngine = SpeechModeAcousticEngine;
